import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Plus, Minus, Trash2, ArrowRight, ShieldCheck, Truck } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { toast } from "sonner";

export default function CartDrawer() {
  const { cart, drawerOpen, closeDrawer, updateItem, removeItem } = useCart();
  const { isAuthenticated } = useAuth();
  const { openAuthModal } = useAuthModal();
  const navigate = useNavigate();
  const items = cart.items || [];

  const subtotal = items.reduce((sum, i) => {
    const v = i.variant;
    const unit = v?.sale_price ?? v?.price_inr ?? 0;
    return sum + unit * (i.quantity || 0);
  }, 0);
  const mrpTotal = items.reduce((sum, i) => {
    const v = i.variant;
    return sum + (v?.mrp_inr || v?.price_inr || 0) * (i.quantity || 0);
  }, 0);
  const savings = Math.max(0, mrpTotal - subtotal);

  const handleQtyChange = async (variantId, newQty) => {
    if (newQty < 1) return;
    try { await updateItem(variantId, newQty); }
    catch { toast.error("Failed to update quantity"); }
  };

  const handleRemove = async (variantId) => {
    try { await removeItem(variantId); toast.success("Removed from cart"); }
    catch { toast.error("Failed to remove item"); }
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={(o) => !o && closeDrawer()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0 bg-white"
        data-testid="cart-drawer"
      >
        <SheetTitle className="sr-only">Your Cart</SheetTitle>
        <SheetDescription className="sr-only">Review items in your cart before checkout</SheetDescription>
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3 bg-white">
          <div className="w-9 h-9 rounded-full bg-gray-900 flex items-center justify-center">
            <ShoppingBag className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-gray-900 text-[15px] leading-tight">Your Cart</h2>
            <p className="text-xs text-gray-500">{items.length} {items.length === 1 ? "item" : "items"}</p>
          </div>
        </div>

        {/* Items list */}
        <div className="flex-1 overflow-y-auto px-5 py-4 bg-gray-50/40">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <ShoppingBag className="w-7 h-7 text-gray-400" />
              </div>
              <p className="font-semibold text-gray-900 mb-1">Your cart is empty</p>
              <p className="text-sm text-gray-500 mb-5">Start adding items you love</p>
              <Button onClick={closeDrawer} className="rounded-full bg-gray-900 text-white hover:bg-gray-800 px-6" data-testid="cart-drawer-continue-shopping">
                Continue Shopping
              </Button>
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map(item => {
                const v = item.variant || {};
                const p = item.product || {};
                const unit = v.sale_price ?? v.price_inr ?? 0;
                const strike = v.mrp_inr && v.mrp_inr > unit ? v.mrp_inr : null;
                const img = p.media?.[0]?.url || "https://placehold.co/200x200/f9fafb/6b7280?text=Item";
                return (
                  <li key={item.variant_id}
                    className="bg-white rounded-xl border border-gray-100 p-3 flex gap-3"
                    data-testid={`drawer-item-${item.variant_id}`}>
                    <Link to={`/product/${p.slug || ""}`} onClick={closeDrawer}
                      className="w-20 h-20 rounded-lg overflow-hidden shrink-0 bg-gray-50">
                      <img src={img} alt={p.title} className="w-full h-full object-cover" />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/product/${p.slug || ""}`} onClick={closeDrawer}>
                        <p className="font-semibold text-[13px] text-gray-900 truncate hover:underline">{p.title}</p>
                      </Link>
                      <p className="text-[11px] text-gray-500 truncate mt-0.5">{v.title}</p>
                      <div className="flex items-baseline gap-1.5 mt-1.5">
                        <span className="text-sm font-bold text-gray-900">₹{Math.round(unit).toLocaleString("en-IN")}</span>
                        {strike && <span className="text-[10px] line-through text-gray-400">₹{strike.toLocaleString("en-IN")}</span>}
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="inline-flex items-center bg-gray-50 border border-gray-200 rounded-full">
                          <button
                            onClick={() => handleQtyChange(item.variant_id, (item.quantity || 1) - 1)}
                            disabled={(item.quantity || 1) <= 1}
                            className="w-7 h-7 flex items-center justify-center text-gray-700 disabled:opacity-40 hover:bg-gray-100 rounded-l-full"
                            data-testid={`drawer-qty-minus-${item.variant_id}`}>
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-7 text-center text-xs font-semibold" data-testid={`drawer-qty-${item.variant_id}`}>{item.quantity}</span>
                          <button
                            onClick={() => handleQtyChange(item.variant_id, (item.quantity || 1) + 1)}
                            className="w-7 h-7 flex items-center justify-center text-gray-700 hover:bg-gray-100 rounded-r-full"
                            data-testid={`drawer-qty-plus-${item.variant_id}`}>
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <button
                          onClick={() => handleRemove(item.variant_id)}
                          className="text-gray-400 hover:text-red-500 p-1 transition-colors"
                          data-testid={`drawer-remove-${item.variant_id}`}
                          aria-label="Remove item">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Trust bar */}
          {items.length > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="flex items-center gap-1.5 bg-white border border-gray-100 rounded-lg px-2.5 py-2">
                <Truck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="text-[10px] font-medium text-gray-700 leading-tight">Free Delivery</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white border border-gray-100 rounded-lg px-2.5 py-2">
                <ShieldCheck className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                <span className="text-[10px] font-medium text-gray-700 leading-tight">Secure checkout</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-gray-100 bg-white px-5 py-4 space-y-3">
            {savings > 0 && (
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-gray-500">You save</span>
                <span className="font-semibold text-emerald-600" data-testid="drawer-savings">₹{Math.round(savings).toLocaleString("en-IN")}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Subtotal</p>
                <p className="text-lg font-bold text-gray-900" data-testid="drawer-subtotal">
                  ₹{Math.round(subtotal).toLocaleString("en-IN")}
                </p>
              </div>
              <p className="text-[10px] text-gray-400 max-w-[50%] text-right">Shipping, taxes & discounts calculated at checkout.</p>
            </div>
            <Link to="/checkout" onClick={(e) => {
              e.preventDefault();
              closeDrawer();
              if (!isAuthenticated) {
                openAuthModal({ onSuccess: () => navigate("/checkout") });
                return;
              }
              navigate("/checkout");
            }} className="block" data-testid="drawer-checkout-link">
              <Button className="w-full h-12 rounded-xl font-black text-[13px] tracking-wide group transition-all hover:shadow-lg"
                style={{ background: "#FFB800", color: "#000000" }}
                data-testid="drawer-checkout-btn">
                CHECKOUT <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </Link>
            <Link to="/cart" onClick={closeDrawer} className="block" data-testid="drawer-view-cart-link">
              <button className="w-full h-10 text-xs font-semibold uppercase tracking-wider text-gray-600 hover:text-gray-900 transition-colors">
                View full cart
              </button>
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
